/* eslint-disable no-underscore-dangle */
import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { AppState } from '../../../../store/store.types';
import { NumberInput, TextInput } from '../../../../components/forms/Input';
import StatusLabel from '../../../../components/forms/StatusLabel';
import Checkbox from '../../../../components/forms/Checkbox';

import {
  CarModelModel,
  ExcessModel,
  fuelPolicyTypeOptions,
  ItemModel,
  MileagePolicyType,
  mileagePolicyTypeOptions,
  mileageUnitPeriodTypeOptions,
  mileageUnitTypeOptions,
  pricingStrategyOptions,
  PricingStrategyType,
  ProductCountryModel,
  productEditRoles,
  ProductItemModel,
  ProductModel,
  ProductRequest,
  RateAdjustmentManagementType,
  rateAdjustmentManagementTypeOptions
} from '../../../../api/core/core.types';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import Select from '../../../../components/forms/Select';
import { SelectedOption, StatusType } from '../../../../api/api.types';
import { addNewProduct, deleteProduct, editProduct, getProduct } from '../../actions/product.actions';
import { ProductActionTypes } from '../../types/product.types';
import { AdminApi } from '../../../../api/api.apiclient';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { getItemList } from '../../actions/item.actions';
import SectionTitle from '../../../../components/forms/SectionTitle';
import RadioButton from '../../../../components/forms/RadioButton';
import { getCarModelsList } from '../../actions/carmodel.actions';
import ProductCarModelsModal, { ProductExcessModel } from '../../../../components/modals/ProductCarModelsModal';
import { ListLinkElement } from '../../../core/types/rates.types';
import { generateCarElementLink } from '../../../../helpers/listview.helper';
import { TwoColumnsSelectValueModel } from '../../../../components/forms/TwoColumnsSelect';
import { MultiSelect } from '../../../../components/forms/MultiSelect';
import { WithTranslation, withTranslation } from 'react-i18next';
import { ClassifierModel, ClassifierTypes } from '../../../../api/admin/admin.types';
import { formatDateTime } from '../../../../helpers/date.helper';
import { Link } from 'react-router-dom';

interface ProductLocationProps {
  id: string;
}

interface ProductDetailsProps extends RouteComponentProps<ProductLocationProps>, WithTranslation {
  selectedProduct: ProductModel | null;
  getSelectedProduct: (id: number) => void;
  addNewProduct: (request: ProductRequest) => void;
  editProduct: (id: number, request: ProductRequest) => void;
  deleteProduct: (id: number) => void;
  getItems: () => void;
  getCarModels: () => void;
  items: ItemModel[];
  carModels: CarModelModel[];
  isNew?: boolean;
  postSuccess: boolean | null;
  lastFetch: number | null;
  user: UserModel | null;
}

interface ProductDetailsState {
  isModalOpen: boolean;
  excessCurrencies: ClassifierModel[];
  countries: ClassifierModel[];
  isEdit: boolean;
  isExcessModalOpen: boolean;
  acrissCodesInUse: Map<number, string[]>;
  selectedExcess?: ProductExcessModel;
  selectedItems: number[];
  selectedCountries: string[];
}

class ProductDetails extends React.Component<ProductDetailsProps, ProductDetailsState> {

  readonly state = {
    isModalOpen: false,
    excessCurrencies: [] as ClassifierModel[],
    countries: [] as ClassifierModel[],
    isEdit: false,
    isExcessModalOpen: false,
    acrissCodesInUse: new Map(),
    selectedExcess: undefined,
    selectedItems: [],
    selectedCountries: []
  };

  form = FormBuilder.group({
    name: [ '', Validators.required ],
    code: [ '', Validators.required ],
    description: [ '' ],
    supplierReferenceCode: [ '' ],
    fuelPolicyType: [ '', Validators.required ],
    mileagePolicyType: [ '', Validators.required ],
    mileageUnitType: [ '' ],
    mileageUnitPeriodType: [ '' ],
    mileageQuantity: [ null ],
    active: [ true ],
    items: [ { items: [], selected: [] } ],
    countries: [ { items: [], selected: [] } ],
    excessModels: [ [] ],
    itemSelection: [ 'none' ],
    countrySelection: [ 'all' ],
    pricingStrategy: [ '' ],
    rateAdjustmentManagementType: [ '' ],
    rateAdjustmentCoefficient: [ '' ]
  });

  async componentDidMount() {
    const { id } = this.props.match.params;
    if (id) {
      this.props.getSelectedProduct(Number(id));
    }

    this.props.getItems();
    this.props.getCarModels();

    await AdminApi.Classifiers.listAllClassifiersByType(ClassifierTypes.CURRENCY).then((response) => {
      this.setState({ excessCurrencies: response });
    });

    await AdminApi.Classifiers.listAllClassifiersByType(ClassifierTypes.COUNTRY).then((response) => {
      this.setState({ countries: response });
    });

    if (this.props.items.length) this.assignItemsToForm(this.props.items);

    if (this.state.countries.length) this.assignCountriesToForm(this.state.countries);
  }

  componentDidUpdate(prevProps: ProductDetailsProps) {
    if ((this.props.isNew) && this.props.postSuccess && this.props.selectedProduct) {
      this.props.history.push(`/setup/products/${this.props.selectedProduct.id}/view`);
    }

    if (this.props.selectedProduct && (!prevProps.selectedProduct || this.props.selectedProduct !== prevProps.selectedProduct)) {
      this.updateFormValue();
    }
  }

  getProduct = async (id: number) => this.props.getSelectedProduct(id);

  onDeleteConfirm = () => {
    const { id } = this.props.match.params;
    this.props.deleteProduct(Number(id));

    this.props.history.push('/setup/products/');
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();

    if (this.form.invalid) {
      return;
    }

    const excessArray: ExcessModel[] = [];

    form.excessModels.value.forEach((em: ProductExcessModel) => excessArray.push(em.excessModel));

    if (this.props.selectedProduct) {
      const request: ProductRequest = {
        ...this.props.selectedProduct,
        name: form.name.value,
        code: form.code.value,
        supplierReferenceCode: form.supplierReferenceCode.value,
        description: form.description.value,
        fuelPolicyType: form.fuelPolicyType.value,
        mileagePolicyType: form.mileagePolicyType.value,
        mileageUnitType: form.mileageUnitType.value || undefined,
        mileageUnitPeriodType: form.mileageUnitPeriodType.value || undefined,
        mileageQuantity: form.mileageQuantity.value,
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
        items: this.state.selectedItems.map((itm) => ({ id: itm })),
        countries: this.state.selectedCountries.map((itm) => ({ countryCode: itm })),
        excesses: excessArray,
        pricingSumByDay: form.pricingStrategy.value === PricingStrategyType.RENTAL_DAYS,
        rateAdjustmentManagementType: form.rateAdjustmentManagementType.value,
        rateAdjustmentCoefficient: form.rateAdjustmentCoefficient.value
      };
      const { id } = this.props.match.params;

      this.props.editProduct(Number(id), request);
      this.setState({ isEdit: false });

    } else {
      const request: ProductRequest = {
        name: form.name.value,
        code: form.code.value,
        supplierReferenceCode: form.supplierReferenceCode.value,
        fuelPolicyType: form.fuelPolicyType.value,
        description: form.description.value,
        mileagePolicyType: form.mileagePolicyType.value,
        mileageUnitType: form.mileageUnitType.value || undefined,
        mileageUnitPeriodType: form.mileageUnitPeriodType.value || undefined,
        mileageQuantity: form.mileageQuantity.value,
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
        items: this.state.selectedItems.map((itm) => ({ id: itm })),
        countries: this.state.selectedCountries.map((itm) => ({ countryCode: itm })),
        excesses: excessArray,
        pricingSumByDay: form.pricingStrategy.value === PricingStrategyType.RENTAL_DAYS,
        rateAdjustmentManagementType: form.rateAdjustmentManagementType.value,
        rateAdjustmentCoefficient: form.rateAdjustmentCoefficient.value
      };

      this.props.addNewProduct(request);
    }
  };

  itemsToOptionArray = (items: ProductItemModel[]) => {
    const options: { value: string; label: string }[] = [];
    items.forEach((i) => options.push({ value: String(i.id), label: i.name }));

    return options;
  };

  mapExcessModels = (product: ProductModel) => {
    const excessArray: ProductExcessModel[] = [];
    product.excesses.forEach((e, index) => excessArray.push({
      id: index,
      excessModel: e
    }));

    return excessArray;
  };

  updateFormValue = () => {
    if (!this.props.selectedProduct) {
      return;
    }

    const product = this.props.selectedProduct;

    if (product) {
      const excessModels = this.mapExcessModels(product);
      this.setState({ acrissCodesInUse: this.mapMarkedAcrissCodes(excessModels) });
      this.form.patchValue({
        name: product.name,
        code: product.code,
        supplierReferenceCode: product.supplierReferenceCode,
        description: product.description,
        fuelPolicyType: product.fuelPolicyType,
        mileagePolicyType: product.mileagePolicyType,
        mileageUnitType: product.mileageUnitType,
        mileageUnitPeriodType: product.mileageUnitPeriodType,
        mileageQuantity: product.mileageQuantity,
        active: product.statusType,
        itemSelection: product.items.length ? 'selection' : 'none',
        countrySelection: product.countries.length ? 'selection' : 'all',
        pricingStrategy: product.pricingSumByDay ? PricingStrategyType.RENTAL_DAYS : PricingStrategyType.RENTAL_DURATION,
        excessModels,
        items: {
          ...this.form.controls.items.value,
          selected: this.props.selectedProduct ? this.props.selectedProduct.items.map((v) => v.id) : this.form.controls.items.value.selected
        },
        countries: {
          ...this.form.controls.countries.value,
          selected: this.props.selectedProduct ? this.props.selectedProduct.countries.map((v) => v.countryCode) : this.form.controls.countries.value.selected
        },
        rateAdjustmentCoefficient: product.rateAdjustmentCoefficient,
        rateAdjustmentManagementType: product.rateAdjustmentManagementType
      });

      this.setState({
        selectedItems: this.props.selectedProduct.items.map((c) => c.id),
        selectedCountries: this.props.selectedProduct.countries.map((c) => c.countryCode)
      });
    }
  };

  mapMarkedAcrissCodes = (excesses: ProductExcessModel[]) => {
    const assignedAcrissCodes = new Map();
    excesses.forEach((em) => assignedAcrissCodes.set(em.id, em.excessModel.acrissCodes));

    return assignedAcrissCodes;
  };

  getLabel = (list: any[], value: string) => {
    const obj = list.find((select) => select.value === value);
    if (obj) {
      return obj.label;
    }

    return value;
  };

  onCancel = () => {
    if (this.props.isNew) {
      this.props.history.push('/setup/products/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  patchField = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option.value });
  };

  assignItemsToForm = (items: ItemModel[]) => {
    const form = this.form.controls.items.value as TwoColumnsSelectValueModel;
    items.forEach((item) => {
      form.items.push(item);
    });

    if (this.props.selectedProduct) {
      const ids = this.props.selectedProduct.items.map((itm: ProductItemModel) => itm.id);
      form.selected = ids;
      this.setState({
        selectedItems: ids
      });
    }
  };

  assignCountriesToForm = (countries: ClassifierModel[]) => {
    const form = this.form.controls.countries.value as TwoColumnsSelectValueModel;
    countries.forEach((country, inx) => {
      const newObj = { ...country, countryCode: country.code, id: inx };
      form.items.push(newObj);
    });

    if (this.props.selectedProduct) {
      const ids = this.props.selectedProduct.countries.map((itm) => itm.countryCode);
      form.selected = ids;
      this.setState({
        selectedCountries: ids
      });
    }
  };

  onNoItemsSelect = () => {
    this.form.controls.items.patchValue({
      selected: []
    });
    this.setState({
      selectedItems: []
    });
  };

  onAllCountriesSelect = () => {
    this.form.controls.countries.patchValue({
      selected: []
    });
    this.setState({
      selectedCountries: []
    });
  };

  getItems = (items: ProductItemModel[]) => {
    const itemsElms: ListLinkElement[] = [];

    let itemList;
    if (items.length === 0) {
      itemList = 'No items';
    } else {
      items.forEach((i) => {
        const item = this.props.items.find((it) => i.id === it.id);
        if (item) itemsElms.push({ label: item ? item.name : String(i.id), linkAddress: `/setup/items/${i.id}/view` });
      });

      // eslint-disable-next-line no-nested-ternary
      itemsElms.sort((a, b) => ((a.label < b.label) ? -1
        : (a.label > b.label) ? 1 : 0));
      itemList = (
        <ul className="details-list">
          {itemsElms.map((item) => (
            <li
              key={item.label}
              className="list-link-item"
              onClick={() => this.props.history.push(item.linkAddress)}
            >
              {
                item.label
              }
            </li>
          ))}
        </ul>
      );
    }

    return itemList;
  };

  getCountries = (countries: ProductCountryModel[]) => {
    const countriesElms: ListLinkElement[] = [];

    let countryList;
    if (countries.length === 0) {
      countryList = 'All countries';
    } else {
      countries.forEach((i) => {
        const country = this.state.countries.find((c) => c.code === i.countryCode);
        if (country) countriesElms.push({
          label: country ? country.name : i.countryCode,
          linkAddress: `/setup/classifiers/${i.countryCode}/view`
        });
      });

      // eslint-disable-next-line no-nested-ternary
      countriesElms.sort((a, b) => ((a.label < b.label) ? -1 : (a.label > b.label) ? 1 : 0));
      countryList = (
        <ul className="details-list">
          {countriesElms.map((country) => (
            <li
              key={country.label}
              className="list-link-item"
              onClick={() => this.props.history.push(country.linkAddress)}
            >
              {
                country.label
              }
            </li>
          ))}
        </ul>
      );
    }

    return countryList;
  };

  saveExcessModel = (excessModel: ExcessModel, id?: number) => {
    const copy: ProductExcessModel[] = JSON.parse(JSON.stringify(this.form.controls.excessModels.value));

    if (id !== undefined) {
      const newExcessArray = copy.filter((c: any) => c.id !== id);
      newExcessArray.push({ id, excessModel });
      this.setState({ acrissCodesInUse: this.mapMarkedAcrissCodes(newExcessArray) });
      this.form.patchValue({ excessModels: newExcessArray });
    } else {
      copy.push({ id: copy.length, excessModel });
      this.setState({ acrissCodesInUse: this.mapMarkedAcrissCodes(copy) });
      this.form.patchValue({ excessModels: copy });
    }

    this.setState({ selectedExcess: undefined, isExcessModalOpen: false });
  };

  removeExcessModel = (id: number) => {
    const copy = JSON.parse(JSON.stringify(this.form.controls.excessModels.value));
    const newExcessArray = copy.filter((c: any) => c.id !== id);
    this.setState({
      acrissCodesInUse: this.mapMarkedAcrissCodes(newExcessArray),
      isExcessModalOpen: false,
      selectedExcess: undefined
    });
    this.form.patchValue({ excessModels: newExcessArray });
  };

  getExcesses = (excesses: ProductExcessModel[], showForm?: boolean) => {
    const { t } = this.props;

    let excessList;
    if (excesses.length === 0) {
      excessList = (
        <div className="form-group row">
        </div>
      );
    } else {
      excessList = (
        <>
          {excesses.map((excess: ProductExcessModel, index: number) => (
            <>
              <div className="form-group row">
                <div className="col-4 d-flex align-items-center justify-content-end">
                  <label>{t('l.product.add_excess.currency')}:</label>
                </div>
                <div className="col-6 d-flex align-items-center justify-content-start">
                  {excess.excessModel.excessCurrencyCode}
                </div>
              </div>
              <div className="form-group row">
                <div className="col-4 d-flex align-items-center justify-content-end">
                  <label>{t('l.product.add_excess.excess_amount')}:</label>
                </div>
                <div className="col-6 d-flex align-items-center justify-content-start">
                  {excess.excessModel.excessAmount}
                </div>
              </div>
              {excess.excessModel.depositAmount != undefined && (
                <div className="form-group row">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label>{t('l.product.add_excess.deposite_amount')}:</label>
                  </div>
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    {excess.excessModel.depositAmount}
                  </div>
                </div>
              )}
              <div className="form-group row">
                <div className="col-4 d-flex align-items-start justify-content-end">
                  <label>{t('l.product.add_excess.car_classes')}:</label>
                </div>
                {excess.excessModel.acrissCodes.length !== 0 && (
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    <ul className="details-list-with-columns">
                      {excess.excessModel.acrissCodes.map((acrissCode: string) => (
                        <li
                          key={acrissCode}
                          className="list-link-item"
                          onClick={() => this.props.history.push(
                            generateCarElementLink(acrissCode, this.props.carModels)
                          )}
                        >
                          {acrissCode}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!excess.excessModel.acrissCodes.length && (
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    <ul className="details-list-with-columns">
                      <div className="form-control-static">All car classes</div>
                    </ul>
                  </div>
                )}
                {showForm && (
                  <div className="col-1 d-flex align-items-center justify-content-start">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={(e) => this.setState({ isExcessModalOpen: true, selectedExcess: excess })}
                    >
                      {t('b.edit_Generic edit button')}
                    </button>
                  </div>
                )}
              </div>
              {index !== excesses.length - 1 ? <div className="horizontal-dividing-line"></div> : ''}
            </>
          ))}
        </>
      );
    }

    return excessList;
  };

  handleItemValueChange = (v: any) => {
    this.setState({ selectedItems: v.map((itm: any) => itm.id) });
  };

  handleCountriesValueChange = (v: any) => {
    this.setState({ selectedCountries: v.map((itm: any) => itm.countryCode) });
  };

  render() {
    if (!this.props.isNew && !this.props.selectedProduct) {
      return null;
    }

    const showForm = this.state.isEdit || this.props.isNew;
    const canEdit = !showForm;

    const { t } = this.props;

    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/setup', label: 'Setup' },
              { path: '/setup/products', label: 'Products' },
              {
                path: null,
                label: this.props.isNew ? 'New' : this.props.selectedProduct?.name || ''
              }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1 className="mb-0">{this.props.isNew ? 'Add new product' : t('page_title.product_details')}</h1>
              {!!this.props.selectedProduct && !showForm && this.props.user && userCanAccessComponent(this.props.user, productEditRoles)
                && (
                  <div>
                    <button type="button" className="btn btn-danger mr-2"
                            onClick={() => this.setState({ isModalOpen: true })}>
                      {t('b.delete_Generic delete button').toUpperCase()}</button>
                    <button type="button" className="btn btn-primary edit-button"
                            onClick={() => this.onEdit()}>{t('b.edit_Generic edit button').toUpperCase()}</button>
                  </div>
                )}
            </div>
          </div>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal pt-1" onSubmit={(e) => this.onSubmit(e)}>
                <SectionTitle title={t('section_title.product.details').toUpperCase()}/>
                <div className="col-12">
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.product.name')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.name.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="name"
                            render={TextInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.product.code')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.code.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="code"
                            render={TextInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.product.description')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.description.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="description"
                            render={TextInput}
                          />
                        )
                      }
                    </div>
                  </div>

                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.product.supplier_ref')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.supplierReferenceCode.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="supplierReferenceCode"
                            render={TextInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.product.fuel_policy')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            {this.getLabel(fuelPolicyTypeOptions, this.form.controls.fuelPolicyType.value)}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="fuelPolicyType"
                            render={(props) => (
                              <Select
                                value={fuelPolicyTypeOptions.find((select) => select.value === this.form.controls.fuelPolicyType.value)}
                                disableClear
                                disableMinWidth
                                className="mr-1"
                                placeholder="Choose Fuel policy type"
                                options={(fuelPolicyTypeOptions)}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="fuel policy type"
                                onChange={(value) => this.patchField(value as SelectedOption, 'fuelPolicyType')}
                              />
                            )}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.product.mileage_policy')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            {this.getLabel(mileagePolicyTypeOptions, this.form.controls.mileagePolicyType.value)}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="mileagePolicyType"
                            render={(props) => (
                              <Select
                                value={mileagePolicyTypeOptions.find((select) => select.value === this.form.controls.mileagePolicyType.value)}
                                disableClear
                                className="mr-1"
                                placeholder="Choose Mileage policy type"
                                options={(mileagePolicyTypeOptions)}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="mileage policy type:"
                                onChange={(value) => this.patchField(value as SelectedOption, 'mileagePolicyType')}
                              />
                            )}
                          />
                        )
                      }
                    </div>
                  </div>
                  {this.form.controls.mileagePolicyType.value === MileagePolicyType.LIMITED && (
                    <>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.product.mileage_units')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          {
                            this.props.selectedProduct && !showForm
                            && (
                              <div className="form-control-static">
                                {this.getLabel(mileageUnitTypeOptions, this.form.controls.mileageUnitType.value)}
                              </div>
                            )
                          }
                          {
                            showForm
                            && (
                              <FieldControl
                                strict={false}
                                name="mileageUnitType"
                                render={(props) => (
                                  <Select
                                    value={mileageUnitTypeOptions.find((select) => select.value === this.form.controls.mileageUnitType.value)}
                                    disableClear
                                    className="mr-1"
                                    placeholder={t('ph.product.mileage.unit_type')}
                                    options={(mileageUnitTypeOptions)}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    label="mileage unit type"
                                    onChange={(value) => this.patchField(value as SelectedOption, 'mileageUnitType')}
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.product.mileage_period')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          {
                            this.props.selectedProduct && !showForm
                            && (
                              <div className="form-control-static">
                                {this.getLabel(mileageUnitPeriodTypeOptions, this.form.controls.mileageUnitPeriodType.value)}
                              </div>
                            )
                          }
                          {
                            showForm
                            && (
                              <FieldControl
                                strict={false}
                                name="mileageUnitPeriodType"
                                render={(props) => (
                                  <Select
                                    value={mileageUnitPeriodTypeOptions.find((select) => select.value === this.form.controls.mileageUnitPeriodType.value)}
                                    disableClear
                                    className="mr-1"
                                    placeholder={t('ph.product.mileage_period')}
                                    options={(mileageUnitPeriodTypeOptions)}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    label="mileage unit period type"
                                    onChange={(value) => this.patchField(value as SelectedOption, 'mileageUnitPeriodType')}
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      </div>
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>{t('l.product.mileage.quantity')}:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          {
                            this.props.selectedProduct && !showForm
                            && (
                              <div className="form-control-static">
                                {this.form.controls.mileageQuantity.value}
                              </div>
                            )
                          }
                          {
                            showForm
                            && (
                              <FieldControl
                                name="mileageQuantity"
                                render={NumberInput}
                              />
                            )
                          }
                        </div>
                      </div>
                    </>
                  )}
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.product.price_model')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            {this.getLabel(pricingStrategyOptions, this.form.controls.pricingStrategy.value)}
                          </div>
                        )
                      }
                      {showForm && (
                        <FieldControl
                          strict={false}
                          name="pricingStrategy"
                          render={(props) => (
                            <Select
                              value={pricingStrategyOptions.find((select) => select.value === this.form.controls.pricingStrategy.value)}
                              disableClear
                              className="mr-1"
                              placeholder="Choose pricing strategy"
                              options={(pricingStrategyOptions)}
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              label="pricing strategy type"
                              onChange={(value) => this.patchField(value as SelectedOption, 'pricingStrategy')}
                            />
                          )}
                        />
                      )}
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.product.rate_adjustments')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            {this.getLabel(rateAdjustmentManagementTypeOptions, this.form.controls.rateAdjustmentManagementType.value)}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="rateAdjustmentManagementType"
                            render={(props) => (
                              <Select
                                value={rateAdjustmentManagementTypeOptions.find((select) => select.value === this.form.controls.rateAdjustmentManagementType.value)}
                                disableClear
                                className="mr-1"
                                placeholder="Choose management type"
                                options={(rateAdjustmentManagementTypeOptions)}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="rate adjustment management type:"
                                onChange={(value) => this.patchField(value as SelectedOption, 'rateAdjustmentManagementType')}
                              />
                            )}
                          />
                        )
                      }
                    </div>
                  </div>
                  {this.form.controls.rateAdjustmentManagementType.value === RateAdjustmentManagementType.AUTOMATIC && (
                      <div className="form-group d-flex">
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          <label>Rate adjustment coefficient:</label>
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                          {
                            this.props.selectedProduct && !showForm
                            && (
                              <div className="form-control-static">
                                {this.form.controls.rateAdjustmentCoefficient.value}
                              </div>
                            )
                          }
                          {
                            showForm
                            && (
                              <FieldControl
                                strict={false}
                                name="rateAdjustmentCoefficient"
                                render={TextInput}
                              />
                            )
                          }
                        </div>
                      </div>
                  )}
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.product.status')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start">
                      {
                        this.props.selectedProduct && !showForm
                        && (
                          <div className="form-control-static">
                            <StatusLabel status={this.props.selectedProduct.statusType}></StatusLabel>
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="active"
                            render={(props) => <Checkbox {...props} id="statusField" className="mt-1"
                                                         disabled={canEdit}>Active</Checkbox>}
                          />
                        )
                      }
                    </div>
                  </div>
                </div>
                <SectionTitle title={t('section_title.product.items').toUpperCase()}/>
                <div className="form-group d-flex">
                  <div className="col-4"/>
                  <div className="col-2 d-flex align-items-center justify-content-start">
                    {this.props.selectedProduct && !showForm && (
                      <div className="form-control-static">
                        {this.getItems(this.props.selectedProduct.items)}
                      </div>
                    )}
                    {showForm && (
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="itemSelection"
                              render={(props) => <RadioButton {...props} onClick={this.onNoItemsSelect}
                                                              fieldValue="none" id="item_none">
                                {t('l.selection.none')}
                              </RadioButton>}
                            />
                            <FieldControl
                              strict={false}
                              name="itemSelection"
                              render={(props) => <RadioButton {...props} fieldValue="selection"
                                                              id="item_sel">
                                {t('l.selection.selected')}</RadioButton>}
                            />
                          </div>
                          {this.form.controls.itemSelection.value === 'selection' && (
                            <FieldControl
                              name="items"
                              render={(v) => (
                                <MultiSelect
                                  {...v}
                                  selectedAttributeName="id"
                                  sortAttributeName="name"
                                  handleValueChange={(val: any) => this.handleItemValueChange(val)}
                                  filterAttributes={[ 'name' ]}
                                  secondaryAtr="name"
                                />
                              )}
                            />
                          )}
                        </div>
                    )}
                  </div>
                </div>
                <SectionTitle title={t('section_title.product.countries').toUpperCase()}/>
                <div className="form-group d-flex mb-2">
                  <div className="col-4"/>
                  <div className="col-2 d-flex align-items-center justify-content-start">
                    {this.props.selectedProduct && !showForm && (
                      <div className="form-control-static">
                        {this.getCountries(this.props.selectedProduct.countries)}
                      </div>
                    )}
                    {showForm && (
                      <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="countrySelection"
                              render={(props) => <RadioButton {...props}
                                                              onClick={this.onAllCountriesSelect}
                                                              fieldValue="all" id="con_all">
                                {t('l.selection.all')}</RadioButton>}
                            />
                            <FieldControl
                              strict={false}
                              name="countrySelection"
                              render={(props) => <RadioButton {...props}
                                                              fieldValue="selection" id="con_sel">
                                {t('l.selection.selected')}
                              </RadioButton>}
                            />
                          </div>
                          {this.form.controls.countrySelection.value === 'selection' && (
                            <FieldControl
                              name="countries"
                              render={(v) => (
                                <MultiSelect
                                  {...v}
                                  selectedAttributeName="countryCode"
                                  sortAttributeName="name"
                                  handleValueChange={(val: any) => this.handleCountriesValueChange(val)}
                                  filterAttributes={[ 'name' ]}
                                  secondaryAtr="name"
                                />
                              )}
                            />
                          )}
                        </div>
                    )}
                  </div>
                </div>
                {!showForm && (
                  <SectionTitle
                    title={t('section_title.product.excess').toUpperCase()}
                  />
                )}
                {showForm && (
                  <SectionTitle
                    title={t('section_title.product.excess').toUpperCase()}
                    showButton
                    buttonName={t('b.product.add_excess').toUpperCase()}
                    onEditClick={() => this.setState({ isExcessModalOpen: true, selectedExcess: undefined })}
                  />
                )}
                {this.getExcesses(this.form.controls.excessModels.value, showForm)}
                {showForm && (
                  <div className="row mt-2">
                    <div className="col-12 text-right">
                      <button type="button" className="btn btn-outline-secondary btn-lg mr-2" onClick={this.onCancel}>
                        {t('b.cancel_Generic cancel button translation').toUpperCase()}
                      </button>
                      <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                        {t('b.save_Generic save button translation').toUpperCase()}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          />
          {this.props.selectedProduct && !showForm && (
            <>
              <SectionTitle title={t('section_title.audit').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-8">
                  <div className="d-flex row info-container">
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedProduct.createdBy ? this.props.selectedProduct.createdBy : 'System'}{this.props.selectedProduct.createdAt ? ` @ ${formatDateTime(this.props.selectedProduct.createdAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedProduct.modifiedBy ? this.props.selectedProduct.modifiedBy : 'System'}{this.props.selectedProduct.modifiedAt ? ` @ ${formatDateTime(this.props.selectedProduct.modifiedAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.log')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      <Link to={`/setup/products/${this.props.selectedProduct.id}/audit`}
                            className="btn btn-text-only"
                            target="_blank"
                            rel="noopener noreferrer">
                        {t('b.car_model.view_audit_log')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <ConfirmationModal
          title="Confirm product removal"
          content="Are you sure you want to remove this product?"
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
        <ProductCarModelsModal
          toggle={() => this.setState({ isExcessModalOpen: false, selectedExcess: undefined })}
          isOpen={this.state.isExcessModalOpen}
          excessModel={this.state.selectedExcess}
          carModels={this.props.carModels}
          currencies={this.state.excessCurrencies}
          onConfirm={(excess: ExcessModel, id?: number) => this.saveExcessModel(excess, id)}
          onCancel={() => this.setState({ isExcessModalOpen: false, selectedExcess: undefined })}
          onDelete={(id: number) => this.removeExcessModel(id)}
          acrissCodesInUse={this.state.acrissCodesInUse}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedProduct: state.setup.products.selectedProduct,
  items: state.setup.items.items,
  carModels: state.setup.carModels.carModels,
  postSuccess: state.setup.products.postSuccess,
  lastFetch: state.setup.products.lastFetch,
  user: state.authentication.user
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, ProductActionTypes>
) => ({
  getSelectedProduct: (id: number) => dispatch(getProduct(id)),
  getItems: () => dispatch(getItemList()),
  addNewProduct: (request: ProductRequest) => dispatch(addNewProduct(request)),
  editProduct: (id: number, request: ProductRequest) => dispatch(editProduct(id, request)),
  deleteProduct: (id: number) => dispatch(deleteProduct(id)),
  getCarModels: () => dispatch(getCarModelsList())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ProductDetails));
